import { useState, useCallback, useEffect, useRef } from 'react';
import superAgentService from '../services/api/superAgentService';
import { SuperAgentStatusResponse } from '../types/superAgent';

// Polling configuration
interface PollingConfig {
  interval: number;        // Polling interval in milliseconds
  maxDuration: number;     // Maximum polling duration in milliseconds
}

const DEFAULT_CONFIG: PollingConfig = {
  interval: 3000,          // 3 seconds (as per design spec)
  maxDuration: 300000,     // 5 minutes (5 * 60 * 1000)
};

export interface UseSuperAgentPollingResult {
  statusData: SuperAgentStatusResponse | null;
  isPolling: boolean;
  error: string | null;
  startPolling: (executionId: number) => void;
  stopPolling: () => void;
  clearError: () => void;
}

/**
 * Custom hook for polling Super Agent execution status
 *
 * Polls the backend every 3 seconds to get execution progress
 * until the execution is completed or failed.
 *
 * @param config - Optional polling configuration
 * @returns Polling state and control functions
 */
export const useSuperAgentPolling = (
  config: Partial<PollingConfig> = {}
): UseSuperAgentPollingResult => {
  const [statusData, setStatusData] = useState<SuperAgentStatusResponse | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingConfig = { ...DEFAULT_CONFIG, ...config };
  const pollingStartTime = useRef<number | null>(null);
  const currentExecutionId = useRef<number | null>(null);

  /**
   * Fetch execution status from API
   */
  const fetchExecutionStatus = useCallback(
    async (executionId: number): Promise<SuperAgentStatusResponse | null> => {
      try {
        const response = await superAgentService.getExecutionStatus(executionId);
        return response;
      } catch (err: any) {
        if (err.response?.status === 404) {
          throw new Error('Execution not found');
        }
        if (err.response?.status === 403) {
          throw new Error('Unauthorized to access this execution');
        }
        throw new Error(err.message || 'Failed to fetch execution status');
      }
    },
    []
  );

  /**
   * Execute polling iteration
   */
  const pollExecution = useCallback(
    async (executionId: number) => {
      try {
        const status = await fetchExecutionStatus(executionId);

        if (!status) {
          throw new Error('No status received');
        }

        setStatusData(status);
        setError(null);

        // Check if polling should continue
        const isRunning = status.execution_status === 'pending' ||
                         status.execution_status === 'running';

        const hasTimeLeft = pollingStartTime.current &&
          (Date.now() - pollingStartTime.current) < pollingConfig.maxDuration;

        if (isRunning && hasTimeLeft && currentExecutionId.current === executionId) {
          // Schedule next poll with fixed interval
          timeoutRef.current = setTimeout(() => {
            pollExecution(executionId);
          }, pollingConfig.interval);
        } else {
          // Polling completed
          setIsPolling(false);

          if (!hasTimeLeft && isRunning) {
            setError('Polling timeout: execution took too long');
          }
        }
      } catch (err: any) {
        setError(err.message);
        setIsPolling(false);
      }
    },
    [fetchExecutionStatus, pollingConfig]
  );

  /**
   * Start polling for execution status
   */
  const startPolling = useCallback(
    (executionId: number) => {
      // Stop any existing polling
      stopPolling();

      // Initialize polling state
      setIsPolling(true);
      setError(null);
      setStatusData(null);
      pollingStartTime.current = Date.now();
      currentExecutionId.current = executionId;

      // Start polling immediately
      pollExecution(executionId);
    },
    [pollExecution]
  );

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsPolling(false);
    pollingStartTime.current = null;
    currentExecutionId.current = null;
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    statusData,
    isPolling,
    error,
    startPolling,
    stopPolling,
    clearError
  };
};
